import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { EntryKind } from '@/types/PlatformAuditLog';

export type PlatformAuditLogPageState = {
  filters: {
    fromDate: string;
    toDate: string;
    entryKind: EntryKind | null;
    actingSuperAdminId: string;
    targetTenantId: string | null;
  };
  cursor: string | null;
  pageSize: number;
  selectedEntryId: string | null;
  tenantPickerFocused: boolean;
};

const initialState: PlatformAuditLogPageState = {
  filters: {
    fromDate: '',
    toDate: '',
    entryKind: null,
    actingSuperAdminId: '',
    targetTenantId: null,
  },
  cursor: null,
  pageSize: 50,
  selectedEntryId: null,
  tenantPickerFocused: false,
};

export const platformAuditLogPageSlice = createSlice({
  name: 'platformAuditLogPage',
  initialState,
  reducers: {
    setFromDate(state, action: PayloadAction<string>) {
      state.filters.fromDate = action.payload;
      state.cursor = null;
    },
    setToDate(state, action: PayloadAction<string>) {
      state.filters.toDate = action.payload;
      state.cursor = null;
    },
    setEntryKind(state, action: PayloadAction<EntryKind | null>) {
      state.filters.entryKind = action.payload;
      // Clear tenant filter when switching to PlatformLanguage (tenant filter irrelevant)
      if (action.payload === 'PlatformLanguage') {
        state.filters.targetTenantId = null;
      }
      state.cursor = null;
    },
    setActingSuperAdminId(state, action: PayloadAction<string>) {
      state.filters.actingSuperAdminId = action.payload;
      state.cursor = null;
    },
    setTargetTenantId(state, action: PayloadAction<string | null>) {
      state.filters.targetTenantId = action.payload;
      state.cursor = null;
    },
    markTenantPickerFocused(state) {
      state.tenantPickerFocused = true;
    },
    advanceCursor(state, action: PayloadAction<string>) {
      state.cursor = action.payload;
    },
    clearFilters(state) {
      state.filters = initialState.filters;
      state.cursor = null;
    },
    toggleSelectedEntryId(state, action: PayloadAction<string>) {
      state.selectedEntryId =
        state.selectedEntryId === action.payload ? null : action.payload;
    },
  },
});

export const {
  setFromDate,
  setToDate,
  setEntryKind,
  setActingSuperAdminId,
  setTargetTenantId,
  markTenantPickerFocused,
  advanceCursor,
  clearFilters,
  toggleSelectedEntryId,
} = platformAuditLogPageSlice.actions;

type AppRootState = {
  platformAuditLogPage: PlatformAuditLogPageState;
};

export const selectPlatformAuditLogPage = (state: AppRootState) =>
  state.platformAuditLogPage;
